import React, { Fragment, useEffect, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import { Link } from "react-router-dom";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";
import JobListGlobalFilter from "./GlobalSearchFilter";

// Column Filter
const Filter = ({ column }) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={columnFilterValue ?? ""}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded px-2 py-1"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <React.Fragment>
      <Col sm={4}>
        <input
          {...props}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </Col>
    </React.Fragment>
  );
};

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  isJobListGlobalFilter,
  isExportButton,
  exportButtonClass,
  exportButtonName,
  handleExportUserClick,
  noDataMessage,
  defaultPageSize = 20, // ✅ default 20 rows per page
  initialSorting
}) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize
  });

  useEffect(() => {
    setPaginationState((prev) =>
      prev.pageSize === defaultPageSize
        ? prev
        : {
            ...prev,
            pageSize: defaultPageSize
          }
    );
  }, [defaultPageSize]);

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: { fuzzy: fuzzyFilter },
    autoResetPageIndex: false,
    state: {
      columnFilters,
      globalFilter,
      pagination: paginationState
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPaginationState,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // ✅ Default page size = 20
    initialState: {
      ...(initialSorting ? { sorting: initialSorting } : {}),
      pagination: { pageSize: defaultPageSize }
    }
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    getState
  } = table;

  return (
    <Fragment>
      <Row className="mb-2 justify-content-end">
        {isCustomPageSize && (
          <Col sm={2}>
            <select
              className="form-select pageSize mb-2"
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )}

        {isGlobalFilter && (
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => setGlobalFilter(String(value))}
            className="form-control search-box me-2 mb-2 d-inline-block"
            placeholder={SearchPlaceholder}
          />
        )}

        {isJobListGlobalFilter && (
          <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />
        )}

        {isAddButton && (
          <Col xs="auto">
            <div className="text-sm-end">
              <Button
                type="button"
                className={buttonClass}
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus me-1"></i> {buttonName}
              </Button>
            </div>
          </Col>
        )}

        {isExportButton && (
          <Col xs="auto">
            <div className="text-sm-end">
              <Button
                type="button"
                className={exportButtonClass}
                onClick={handleExportUserClick}
              >
                <i className="mdi mdi-microsoft-excel me-1"></i>{" "}
                {exportButtonName}
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className={divClassName ? divClassName : "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`${
                        header.column.columnDef.enableSorting
                          ? "sorting sorting_desc"
                          : ""
                      }`}
                    >
                      {header.isPlaceholder ? null : (
                        <React.Fragment>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : "",
                              onClick: header.column.getToggleSortingHandler()
                            }}
                          >
                            {typeof header.column.columnDef.header === 'function' 
                              ? header.column.columnDef.header() 
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {
                              {
                                asc: "",
                                desc: ""
                              }[header.column.getIsSorted()] ?? null
                            }
                          </div>
                          {header.column.getCanFilter() ? (
                            <div>
                              <Filter column={header.column} table={table} />
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {table && table.getRowModel && table.getCoreRowModel ? (
              table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center py-3 text-muted"
                  >
                    {table.getCoreRowModel().rows.length === 0
                      ? noDataMessage || "No records found"
                      : "No records found"}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      const cellValue = flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      );
                      return (
                        <td key={cell.id}>
                          {cellValue !== null &&
                          cellValue !== undefined &&
                          cellValue !== "" ? (
                            cellValue
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-3 text-muted"
                >
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className="justify-content-md-end justify-content-center align-items-center">
          <Col>
            <div className="dataTables_info">
              Showing {getState().pagination.pageSize} of {data.length} Results
            </div>
          </Col>
          <Col className="col-md-auto">
            <div className={paginationWrapper}>
              <ul className={pagination}>
                <li
                  className={`paginate_button page-item previous ${
                    !getCanPreviousPage() ? "disabled" : ""
                  }`}
                >
                  <Link to="#" className="page-link" onClick={previousPage}>
                    <i className="mdi mdi-chevron-left"></i>
                  </Link>
                </li>

                {/* Pagination with max 5 pages and ellipsis */}
                {(() => {
                  const pageOptions = getPageOptions();
                  const pageIndex = getState().pagination.pageIndex;
                  const totalPages = pageOptions.length;
                  let start = Math.max(0, pageIndex - 2);
                  let end = Math.min(totalPages, start + 5);
                  if (end - start < 5) start = Math.max(0, end - 5);
                  const visiblePages = pageOptions.slice(start, end);
                  const items = [];

                  if (start > 0) {
                    items.push(
                      <li key={0} className={`paginate_button page-item`}>
                        <Link
                          to="#"
                          className="page-link"
                          onClick={() => setPageIndex(0)}
                        >
                          1
                        </Link>
                      </li>
                    );
                    if (start > 1) {
                      items.push(
                        <li
                          key="start-ellipsis"
                          className="paginate_button page-item disabled"
                        >
                          <span className="page-link">...</span>
                        </li>
                      );
                    }
                  }

                  visiblePages.forEach((item) => {
                    items.push(
                      <li
                        key={item}
                        className={`paginate_button page-item ${
                          pageIndex === item ? "active" : ""
                        }`}
                      >
                        <Link
                          to="#"
                          className="page-link"
                          onClick={() => setPageIndex(item)}
                        >
                          {item + 1}
                        </Link>
                      </li>
                    );
                  });

                  if (end < totalPages) {
                    if (end < totalPages - 1) {
                      items.push(
                        <li
                          key="end-ellipsis"
                          className="paginate_button page-item disabled"
                        >
                          <span className="page-link">...</span>
                        </li>
                      );
                    }
                    items.push(
                      <li
                        key={totalPages - 1}
                        className={`paginate_button page-item`}
                      >
                        <Link
                          to="#"
                          className="page-link"
                          onClick={() => setPageIndex(totalPages - 1)}
                        >
                          {totalPages}
                        </Link>
                      </li>
                    );
                  }

                  return items;
                })()}

                <li
                  className={`paginate_button page-item next ${
                    !getCanNextPage() ? "disabled" : ""
                  }`}
                >
                  <Link to="#" className="page-link" onClick={nextPage}>
                    <i className="mdi mdi-chevron-right"></i>
                  </Link>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;
